/**
 * Der Weg vom ResourceRef ins Quellsystem — der Platzhalter für `LNK-001`.
 *
 * **Was hier steht, ist ausdrücklich nicht die Verweisregistry.** `04` §4.2
 * beschreibt den ResourceRef als „das einzige systemübergreifende Bindeglied";
 * was aus ihm einen aufrufbaren Deep Link macht, ist eine Registry, die je
 * Mandant weiß, unter welcher Adresse ein Dienst läuft. Die gehört `LNK-001`
 * und ist nicht begonnen.
 *
 * Bis dahin zeigen die Verweise hier auf die **Attrappenseiten** aus
 * `tests/ui/attrappen`. Der Grund ist derselbe wie beim Prüfrezept für die
 * Rahmenansicht: Ein Ablauf, der auf den echten Dienst wartet, wird nie
 * durchgespielt — und dann fällt erst beim Piloten auf, was daran nicht
 * trägt. Der Sprung aus dem Auftrag ins Quellsystem ist einer der Wege, um die
 * es dem Produkt geht; er muss bedienbar sein, bevor Nextcloud läuft.
 *
 * **Woran man merkt, dass es eine Attrappe ist.** Die Seite sagt es selbst,
 * die Adresse trägt den Attrappenport, und die Oberfläche kennzeichnet den
 * Verweis. Drei Stellen, weil eine überklebt werden kann, ohne dass es
 * auffällt — und eine Attrappe, die für den echten Dienst gehalten wird, ist
 * schlimmer als gar keine.
 *
 * Die Import-Grenze aus ADR-016 gilt: kein React, kein HTTP, keine Node-API.
 */

import type { ResourceRef } from "@hwc/contracts";

/**
 * Wo die Attrappen liegen.
 *
 * Überschreibbar wie die Austauschadresse und aus demselben Grund: `pnpm demo`
 * startet sie auf 4310, aber ein belegter Port verschiebt sie. Ein Verweis auf
 * einen Dienst, der dort nicht läuft, ist besser als einer, der stillschweigend
 * einen fremden trifft.
 */
const VORGABE = "http://localhost:4310";

let basis = VORGABE;

export function setzeQuellenbasis(neu: string): void {
  basis = neu.replace(/\/$/, "");
}

export function quellenbasis(): string {
  return basis;
}

/**
 * Die Adressen der laufenden Dienste, sofern es welche gibt.
 *
 * Aus der Laufzeitkonfiguration nach ADR-044 — dasselbe `domains`-Feld, das
 * `portal_konfiguration.py` je Kunde erzeugt. Steht dort nichts, bleibt es bei
 * den Attrappen: Der lokale Demostand hat keinen Kundenstack, und ein Verweis
 * auf eine Adresse aus einer fremden Konfiguration wäre schlimmer als einer auf
 * eine erkennbare Attrappe.
 */
let dienste: Readonly<Record<string, string>> = {};

export function setzeDienstadressen(neu: Readonly<Record<string, string>>): void {
  dienste = neu;
}

/**
 * **Ob ein Dienst sich rahmen lässt — gemessen, nicht angenommen.**
 *
 * ADR-040 sieht vor, dass die Shell fremde Oberflächen rahmt. Am 05.09.2026
 * am Piloten gemessen, mit `curl -I` gegen jeden Namen:
 *
 *   Nextcloud    `X-Frame-Options: SAMEORIGIN`  auf **allen** Pfaden
 *   Bulwark      `X-Frame-Options: DENY`
 *   Paperless    `X-Frame-Options: SAMEORIGIN`
 *   Vikunja      keine Sperre
 *
 * Drei von vier verweigern es also in ihrer Standardkonfiguration. Die
 * Attrappe `/verweigert` nennt diesen Fall „den Normalfall im Fehlerfall" —
 * er ist der Normalfall überhaupt.
 *
 * **Was hier steht, ist eine Messung mit Datum und kein Dauerzustand.** Alle
 * drei lassen sich umkonfigurieren; ob man das tut, ist eine Entscheidung, die
 * ADR-040 treffen muss und die hier nicht vorweggenommen wird. Bis dahin
 * verweist das Portal, statt einen leeren Kasten zu zeigen.
 */
const RAHMBAR: Readonly<Record<string, boolean>> = {
  vikunja: true,
  nextcloud: false,
  stalwart: false,
  paperless: false,
};

export function laesstSichRahmen(ref: ResourceRef): boolean {
  // Ohne laufenden Dienst wird die Attrappe gerahmt, und die lässt sich rahmen.
  if (!dienstadresse(ref)) return true;
  return RAHMBAR[ref.provider] ?? false;
}

/**
 * Die Basisadresse des Dienstes, der diese Ressource ausliefert — oder
 * `undefined`, wenn keiner konfiguriert ist.
 *
 * Eine Mail liegt in Stalwart und wird in Bulwark gelesen; die Adresse ist
 * deshalb die des Webmailers, nicht die des Mailservers. `04` §4.2 trennt
 * beides ohnehin: Der ResourceRef benennt die Datenhaltung, nicht den Weg
 * dorthin.
 */
const DIENSTSCHLUESSEL: Readonly<Record<string, string>> = {
  // Seit dem 05.09.2026 gibt es einen eigenen Namen für Nextcloud. Bis dahin
  // stand hier nichts, weil `portal` im Piloten zufällig auf Nextcloud zeigte
  // und im Entwicklungsstand auf das Portal selbst — eine Zuordnung, die an
  // einer Stelle richtig und überall sonst falsch gewesen wäre.
  nextcloud: "cloud",
  stalwart: "webmail",
  vikunja: "vikunja",
  paperless: "paperless",
  keycloak: "auth",
};

export function dienstadresse(ref: ResourceRef): string | undefined {
  const schluessel = DIENSTSCHLUESSEL[ref.provider];
  if (!schluessel) return undefined;
  const name = dienste[schluessel];
  if (!name) return undefined;

  /*
   * **`.invalid` ist kein Dienst.**
   *
   * Die Entwicklungskonfiguration in `apps/portal/vite.config.ts` trägt
   * `aufgaben.beispiel.invalid` und ähnliche Namen — bewusst erfunden, und
   * `.invalid` ist nach RFC 2606 genau dafür reserviert. Ohne diese Zeile
   * hielt das Portal sie für laufende Dienste und schrieb „Vikunja läuft und
   * ist erreichbar" über einen Namen, den es nirgends gibt.
   *
   * Aufgefallen am 05.09.2026 unmittelbar nach dem Bauen: Gegen den Piloten
   * gemessen sah alles richtig aus, weil dort echte Namen stehen. Die
   * Attrappe im Entwicklungsstand war die ehrlichere Prüfung.
   */
  if (name.endsWith(".invalid") || name.includes(".invalid:")) return undefined;

  // Die Konfiguration führt Namen, keine URLs — `localhost:4300` im
  // Entwicklungsstand zeigt, warum das Schema nicht darin stehen darf.
  return name.startsWith("http") ? name : `https://${name}`;
}

/**
 * Der Anzeigename des führenden Systems zu einem Provider.
 *
 * `01` §1.3 vergibt je Datentyp genau ein führendes System; der Provider im
 * ResourceRef benennt es technisch, hier steht der Name, den ein Mensch kennt.
 */
const SYSTEMNAME: Readonly<Record<string, string>> = {
  nextcloud: "Nextcloud",
  vikunja: "Vikunja",
  stalwart: "Stalwart",
  paperless: "Paperless",
  keycloak: "Keycloak",
  portal: "Portal",
};

export function systemname(ref: ResourceRef): string {
  return SYSTEMNAME[ref.provider] ?? ref.provider;
}

/**
 * Die Oberfläche, die man beim Öffnen tatsächlich zu sehen bekommt.
 *
 * **Das ist nicht immer das führende System.** Eine Mail liegt in Stalwart —
 * `01` §1.3 weist ihm den Datentyp zu, und der ResourceRef sagt das auch. Wer
 * sie aber liest oder beantwortet, tut das in **Bulwark**: ADR-019 betreibt
 * es unverändert und bindet es über Plugins an. Stalwart hat gar keine
 * Oberfläche, die man rahmen könnte.
 *
 * Die Unterscheidung steht hier und nicht in der Ansicht, weil sie sonst an
 * jeder Stelle neu getroffen würde, an der etwas geöffnet wird — und zwei
 * Stellen, die dasselbe entscheiden, entscheiden es irgendwann verschieden.
 */
const OBERFLAECHE: Readonly<Record<string, string>> = {
  stalwart: "Bulwark",
};

export function oberflaecheZu(ref: ResourceRef): string {
  return OBERFLAECHE[ref.provider] ?? systemname(ref);
}

/**
 * Ob sich diese Ressource überhaupt in einem Fremdsystem öffnen ließe.
 *
 * **Was das Portal selbst führt, hat kein Quellsystem** — eine Kundenakte
 * aufzumachen hieße, das Portal in sich selbst zu rahmen. `04` §4.4 weist den
 * Kundenstamm keinem Fremddienst zu, und ein Verweis dorthin wäre nicht bloß
 * überflüssig, sondern irreführend.
 */
export function hatQuellsystem(ref: ResourceRef): boolean {
  return ref.provider !== "portal";
}

/**
 * Wo im jeweiligen Dienst man landet, solange es keinen Tiefverweis gibt.
 *
 * Die Dateiliste statt der Datei, der Posteingang statt der Mail. Das ist
 * weniger, als `07` §7.12 verspricht — „der primäre Zugriff erfolgt über den
 * Auftrag" meint die Sache selbst —, aber es ist wahr, und der Weg dorthin ist
 * bereits der, den der Adapter später nimmt.
 */
const ANWENDUNGSPFAD: Readonly<Record<string, string>> = {
  nextcloud: "/index.php/apps/files/",
  stalwart: "/",
  vikunja: "/",
  paperless: "/documents",
};

/** Wie eine Ressource beim Öffnen antworten soll — für den Demostand. */
export type Quellfall = "vorhanden" | "verboten" | "weg";

/**
 * Baut die Adresse, unter der die Ressource im Quellsystem läge.
 *
 * `name` und `pfad` gehen als Suchparameter mit, damit die Attrappe zeigen
 * kann, was sie ausliefern würde. **Ein echter Dienst bekäme das nicht** — er
 * kennt seine Ressource selbst und braucht vom Portal nur die Kennung. Der
 * Unterschied ist im Aufruf sichtbar und soll es sein: Wer diese Funktion
 * später durch die Registry ersetzt, sieht an den Parametern, was wegfällt.
 */
export function quelladresse(
  ref: ResourceRef,
  angaben: { readonly name?: string; readonly pfad?: string; readonly fall?: Quellfall } = {},
): string {
  /*
   * **Ein laufender Dienst schlägt die Attrappe.**
   *
   * Was dann entsteht, ist die Adresse der **Anwendung**, nicht der Ressource:
   * Die Kennungen des Demobestands gibt es dort nicht, und ein Deep Link auf
   * `f-01n001` liefe ins Leere. Der echte Tiefverweis entsteht mit dem
   * jeweiligen Adapter und der Verweisregistry aus `LNK-001`; bis dahin ist
   * dies ein Sprung in den Dienst, nicht auf das Dokument — und die Ansicht
   * sagt das auch.
   */
  const echt = dienstadresse(ref);
  if (echt) return `${echt}${ANWENDUNGSPFAD[ref.provider] ?? "/"}`;

  const teile = new URLSearchParams({
    // Die Oberfläche, nicht die Datenhaltung: Wer eine Mail öffnet, landet in
    // Bulwark, auch wenn sie in Stalwart liegt.
    system: oberflaecheZu(ref),
    kennung: ref.opaqueId,
  });
  if (angaben.name) teile.set("name", angaben.name);
  if (angaben.pfad) teile.set("pfad", angaben.pfad);
  if (angaben.fall && angaben.fall !== "vorhanden") teile.set("fall", angaben.fall);

  return `${basis}/quelle?${teile.toString()}`;
}
